<template>
  <div class="max-w-7xl mx-auto p-8">
    <div class="grid grid-cols-12 mb-4">
      <label
        class="col-span-2 pt-2 pr-2 text-right"
        for="school_name">
        学校名：
      </label>
      <div class="col-span-8">
        <select
          id="school_name"
          v-model="filterState.schoolId"
          class="border border-gray-300 outline-none rounded-md bg-white placeholder-gray-400 text-gray-500 focus:outline-none">
          <option
            v-for="school in schools"
            :key="school.id"
            :value="school.id">
            {{ school.name }}
          </option>
        </select>
      </div>
    </div>
    <div class="grid grid-cols-12 mb-4">
      <label
        class="col-span-2 pt-2 pr-2 text-right"
        for="grade">
        学年：
      </label>
      <div class="col-span-8">
        <select
          id="grade"
          v-model="filterState.grade"
          class="border border-gray-300 outline-none rounded-md bg-white placeholder-gray-400 text-gray-500 focus:outline-none">
          <option
            v-for="(grade, i) in gradesList"
            :key="i"
            :value="i">
            {{ grade }}
          </option>
        </select>
      </div>
    </div>
    <div class="grid grid-cols-12 mb-4">
      <label
        class="col-span-2 pt-2 pr-2 text-right"
        for="is_subscribe">
        権限有無：
      </label>
      <div class="col-span-8">
        <select
          id="is_subscribe"
          v-model="filterState.subscriptionStatus"
          class="border border-gray-300 outline-none rounded-md bg-white placeholder-gray-400 text-gray-500 focus:outline-none">
          <option
            v-for="(status, i) in subscriptionStatusList"
            :key="i"
            :value="i">
            {{ status }}
          </option>
        </select>
      </div>
    </div>
    <div class="flex flex-row-reverse">
      <button
        class="px-8 py-2 mr-12 mb-6 rounded bg-green-500 text-white max-w-max shadow-sm hover:shadow-lg"
        @click.prevent="onClickedSaveButton">
        保存
      </button>
    </div>
    <div v-if="students?.length">
      <table class="w-full table-auto">
        <thead>
          <tr class="text-sm leading-normal">
            <template
              v-for="(title, key) in headers"
              :key="`header-${key}`">
              <th
                v-if="key == 'is_subscribe'"
                class="whitespace-nowrap bg-gray-200 text-gray-600 py-3 px-6 text-center">
                {{ title }}
                <input
                  v-model="state.isAllSubscribeChecked"
                  type="checkbox"
                  :checked="state.isAllSubscribeChecked"
                  class="ml-2 bg-gray-50 border-gray-300 focus:ring-3 focus:ring-blue-300 h-4 w-4 rounded focus:outline-none" />
              </th>
              <th
                v-else
                class="whitespace-nowrap bg-gray-200 text-gray-600 py-3 px-6 text-center">
                {{ title }}
              </th>
            </template>
          </tr>
        </thead>
        <template
          v-for="(student, key) in state.students"
          :key="`row-${key}`">
          <tr
            class="border-b border-gray-200 text-gray-600 text-sm font-light">
            <td
              v-for="(title, headerKey) in headers"
              :key="`row-${headerKey}`"
              class="focus:cursor-grab py-3 text-center whitespace-nowrap">
              <div v-if="headerKey == 'is_subscribe'">
                <input
                  v-model="state.students[key][headerKey]"
                  type="checkbox"
                  :checked="state.students[key][headerKey]"
                  class="ml-2 bg-gray-50 border-gray-300 focus:ring-3 focus:ring-blue-300 h-4 w-4 rounded focus:outline-none" />
              </div>
              <div v-else-if="headerKey == 'service_years'">
                {{ student[headerKey] }}
              </div>
              <div v-else>
                {{ student[headerKey] }}
              </div>
            </td>
          </tr>
        </template>
      </table>
    </div>
  </div>
</template>

<script setup>

import { Inertia } from '@inertiajs/inertia'
import {
  reactive, watch, watchEffect, computed, onMounted,
} from 'vue'

const props = defineProps({
  schools: {
    type: Object,
    default: () => ({}),
  },
  schoolId: {
    type: Number,
    default: null,
  },
  schoolYears: {
    type: Number,
    default: null,
  },
  students: {
    type: Object,
    default: () => ({}),
  },
  grade: {
    type: Number,
    default: 0,
  },
  subscriptionStatus: {
    type: Number,
    default: 0,
  },
})
const headers = {
  id: 'ID',
  student_id: '学籍番号',
  service_years: '学年',
  department: '学部・学科',
  name: '名前',
  is_subscribe: '権限',
}

const state = reactive({
  students: props.students,
  isAllSubscribeChecked: false,
})

const filterState = reactive({
  schoolId: props.schoolId,
  grade: props.grade,
  subscriptionStatus: props.subscriptionStatus,
})

const gradesList = ['全学年', '1年', '2年', '3年', '4年']
const subscriptionStatusList = ['選択しない', '権限あり', '権限なし']

const onClickedSaveButton = async () => {
  Inertia.visit(route('subscription.update-status'), {
    method: 'post',
    data: {
      students: state.students,
    },
  })
}

/**
 * 全ての is_subscribe が true なら true, そうでなければ false
 */
const checkAllSubscribe = (() => {
  state.isAllSubscribeChecked = state.students.length > 0 && state.students.every(student => student.is_subscribe)
})

/**
 * 全てのチェックボックスの状態を一括変更
 */
const toggleAllSubscriptions = (newValue) => {
  state.students = state.students.map((student) => ({
    ...student,
    is_subscribe: newValue,
  }))
}

watch(
  () => filterState,
  () => {
    Inertia.get(
      route('school'), {
        sort: props.sort,
        order: props.order,
        showTabDisplayNum: 2,
        ...filterState,
      },
    )
  },
  { deep: true },
)

watch(
  () => state.isAllSubscribeChecked,
  () => {
    if (state.isAllSubscribeChecked) {
      toggleAllSubscriptions(true)
    }
    if (state.isAllSubscribeChecked === false) {
      if ((props.students.length - 1) === state.students.filter(student => student.is_subscribe).length) {
        return
      }

      toggleAllSubscriptions(false)
    }
  },
  { immediate: false },
)

watch(
  () => state.students,
  () => {
    checkAllSubscribe()
  },
  {
    immediate: false,
    deep: true,
  },
)

</script>
